import { NgClass, NgIf } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, forwardRef, inject } from '@angular/core';

import { DsCheckboxSize } from '@design-system/ds-checkbox/types/ds-checkbox-size.type';
import { DsCheckboxChange } from '@design-system/ds-checkbox/interfaces/ds-checkbox-change.interface';

@Component({
  selector: 'ds-checkbox',
  templateUrl: './ds-checkbox.component.html',
  styleUrls: ['./ds-checkbox.component.scss'],
  standalone: true,
  imports: [MatCheckboxModule, NgClass, NgIf],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsCheckboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsCheckboxComponent implements ControlValueAccessor {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Controls the size variant.
   *
   * @default 'sm'
   */
  @Input() size: DsCheckboxSize = 'sm';

  /**
   * Optional primary checkbox text.
   * If not provided, projected content is rendered.
   */
  @Input() label: string | null = null;

  /**
   * Optional secondary text rendered below label.
   */
  @Input() subtitle: string | null = null;

  /**
   * Emits when checkbox checked state changes.
   */
  @Output() change = new EventEmitter<DsCheckboxChange>();

  /** Internal disabled state controlled by Angular forms. */
  isDisabled = false;

  /** Internal checked value controlled by Angular forms. */
  value = false;

  /** ControlValueAccessor callback for value updates. */
  private onChange: (value: boolean) => void = (): void => {};

  /** ControlValueAccessor callback for touch updates. */
  private onTouched: () => void = (): void => {};

  /**
   * Writes a new value from Angular forms API.
   *
   * @param value New checkbox state
   */
  writeValue(value: boolean): void {
    this.value = !!value;

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Registers callback invoked when value changes.
   *
   * @param fn Callback provided by Angular forms API
   */
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers callback invoked when control is touched.
   *
   * @param fn Callback provided by Angular forms API
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Updates disabled state from Angular forms API.
   *
   * @param isDisabled Whether control must be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Toggles checkbox when wrapper area is clicked.
   * Ignores clicks coming from the internal material checkbox to avoid double updates.
   *
   * @param event - wrapper click event
   */
  onWrapperToggle(event: Event): void {
    if (this.isDisabled) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('mat-checkbox')) {
      return;
    }

    this.updateValue(!this.value);
  }

  /**
   * Handles material checkbox value updates.
   *
   * @param event Material checkbox change payload
   */
  onCheckboxChange(event: MatCheckboxChange): void {
    if (this.isDisabled) {
      return;
    }

    this.updateValue(event.checked);
  }

  /**
   * Updates local value and propagates it through CVA API.
   *
   * @param checked - next checkbox state
   */
  private updateValue(checked: boolean): void {
    this.value = checked;
    this.changeDetectorRef.markForCheck();
    this.onChange(this.value);
    this.onTouched();
    this.change.emit({ checked: this.value });
  }
}
